import { format, intervalToDuration, parse, parseISO, type Duration, type Locale } from 'date-fns'
import { de, enGB, fr, nl } from 'date-fns/locale'

export const localeMap: Record<string, Locale> = {
  en: enGB,
  fr,
  de,
  nl
}

export function useDateFormatter() {
  const fileNameDate = (): string => {
    const date = new Date()
    const month = String(date.getUTCMonth() + 1).padStart(2, '0')
    const day = String(date.getUTCDate()).padStart(2, '0')
    const year = date.getUTCFullYear()
    const hours = String(date.getUTCHours()).padStart(2, '0')
    const minutes = String(date.getUTCMinutes()).padStart(2, '0')
    const seconds = String(date.getUTCSeconds()).padStart(2, '0')
    return `${month}-${day}-${year}-${hours}-${minutes}-${seconds}`
  }

  const formatIDMDate = (dateString: string, localeCode: string): string => {
    try {
      const date = new Date(dateString)
      const locale = localeCode ? localeMap[localeCode] : undefined
      return format(date, 'Pp', { locale })
    } catch {
      return ''
    }
  }

  const formatDate = (dateString: string | null | undefined, formatPattern: string = 'dd/MM/yyyy HH:mm:ss', localeCode?: string): string | null => {
    if (!dateString) {
      return null
    }
    const locale = localeCode ? localeMap[localeCode] : undefined
    return format(parseISO(dateString), formatPattern, { locale })
  }

  const formatDateByLocale = (dateString: string | null | undefined, localeCode: string, withSeconds: boolean = false): string | null => {
    if (!dateString) {
      return null
    }
    const formatPattern = withSeconds ? 'Ppp' : 'Pp'
    const locale = localeMap[localeCode] || enGB
    return format(parseISO(dateString), formatPattern, { locale })
  }

  const formatDateFromUSFormat = (dateString: string | null | undefined, formatPattern: string = 'dd/MM/yyyy HH:mm:ss', localeCode?: string): string | null => {
    if (!dateString) {
      return null
    }
    const locale = localeCode ? localeMap[localeCode] : undefined
    try {
      const parsedDate = parse(dateString, 'M/d/yyyy h:mm:ss a', new Date())
      return format(parsedDate, formatPattern, { locale })
    } catch {
      return null
    }
  }

  return {
    formatIDMDate,
    formatDate,
    formatDateByLocale,
    formatDateFromUSFormat,
    fileNameDate
  }
}

export function getDuration(date1: Date | null, date2: Date | null): Duration | null {
  if (!date1 || !date2) {
    return null
  }
  return intervalToDuration({
    start: date1,
    end: date2
  })
}

export function getFormattedDuration(date1: Date | null, date2: Date | null, showMs: boolean = false): string | null {
  if (!date1 || !date2) {
    return null
  }

  const d1 = typeof date1 === 'string' ? new Date(date1) : date1
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2

  const duration = intervalToDuration({
    start: d1,
    end: d2
  })

  const totalMs = Math.abs(d2.getTime() - d1.getTime())
  const ms = totalMs % 1000

  const parts: string[] = []

  if (duration.years) {
    parts.push(`${duration.years}y`)
  }

  if (duration.months) {
    parts.push(`${duration.months}mo`)
  }

  if (duration.days) {
    parts.push(`${duration.days}d`)
  }

  if (duration.hours) {
    parts.push(`${duration.hours}h`)
  }

  if (duration.minutes) {
    parts.push(`${duration.minutes}m`)
  }

  if (duration.seconds) {
    parts.push(`${duration.seconds}s`)
  }

  if (showMs && ms) {
    parts.push(`${ms}ms`)
  }

  return parts.slice(0, 3).join(' ')
}

export function formatDateForQuery(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0')

  const day = pad(date.getDate())
  const month = pad(date.getMonth() + 1)
  const year = date.getFullYear()
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())

  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`
}

export function formatDateForQueryUTC(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0')

  const day = pad(date.getUTCDate())
  const month = pad(date.getUTCMonth() + 1)
  const year = date.getUTCFullYear()
  const hours = pad(date.getUTCHours())
  const minutes = pad(date.getUTCMinutes())
  const seconds = pad(date.getUTCSeconds())

  return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`
}

export function decimalToDuration(decimal: number, unit: 'day' | 'hour' | 'minute' = 'day'): string {
  let totalSeconds: number
  const hourFactor = 3600
  const minuteFactor = 60
  const dayFactor = 86400

  switch (unit) {
    case 'hour':
      totalSeconds = Math.round(decimal * hourFactor)
      break
    case 'minute':
      totalSeconds = Math.round(decimal * minuteFactor)
      break
    case 'day':
    default:
      totalSeconds = Math.round(decimal * dayFactor)
      break
  }

  const hours = Math.floor(totalSeconds / hourFactor)
  const minutes = Math.floor((totalSeconds % hourFactor) / minuteFactor)
  const seconds = totalSeconds % minuteFactor

  const pad = (num: number) => String(num).padStart(2, '0')
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

export function primeVueDateFormat(localeCode: string) {
  switch (localeCode) {
    case 'en':
      return 'mm/dd/yy'
    case 'fr':
      return 'dd/mm/yy'
    case 'de':
      return 'dd.mm.yy'
    case 'nl':
      return 'dd-mm-yy'
    default:
      return 'mm/dd/yy'
  }
}

export function timeToMinutes(time: string): number {
  if (!time) return 0

  const [hStr, mStr = '0'] = time.trim().split(':')
  const hours = Number(hStr)
  const minutes = Number(mStr)

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 0
  }
  return hours * 60 + minutes
}

export function getCurrentDayOfWeek(): string | undefined {
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
  const today = new Date()
  return daysOfWeek[today.getDay()]
}

export function parseDateFilterValue(value: unknown): Date | null {
  if (value instanceof Date) {
    return value
  }
  if (value) {
    return new Date(value as string)
  }
  return null
}
